import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {IonicModule, ModalController} from '@ionic/angular';
import {PhotoBoxComponent} from '../../photo-box/photo-box.component';
import {TranslatePipe} from '@ngx-translate/core';
import {MapPrimaryButtonComponent} from '../map-primary-button/map-primary-button.component';
import {MapToggleBarComponent} from '../map-toggle-bar/map-toggle-bar.component';
import {MapStatisticCirclesComponent} from '../map-statistic-circles/map-statistic-circles.component';
import {MapMomentComponent} from "../map-moment/map-moment.component";
import {MapMarkerModel} from "../../../models/map/map-marker/map-marker.model";
import {CompanyModel} from "../../../models/company.model";
import {TeamModel} from "../../../models/team.model";
import {CompanyService} from "../../../services/company/company.service";
import {ApiResponse, handleError} from "../../../services/utils";
import {NgForOf, NgIf, NgSwitch, NgSwitchCase} from "@angular/common";
import {ActionPointListModule} from "../../action-point-list/action-point-list.module";
import {CommonMyApModule} from "../../common-my-ap/common-my-ap.module";
import {TeamListModule} from "../../team-list/team-list.module";
import {FollowerListModule} from "../../follower-list/follower-list.module";
import {CompanyEmployeeModule} from "../../company-employee/company-employee.module";
import {UserModel} from "../../../models/user.model";
import {AuthService} from "../../../services/auth/auth.service";
import {SidePanelDataService} from "../../../services/z-map/side-panel-data-service";
import {MapSecondaryButtonComponent} from "../map-secondary-button/map-secondary-button.component";
import {FeedService} from "../../../services/feed/feed.service";
import {GlobalService} from "../../../services/global/global.service";
import {PostModel} from "../../../models/post.model";
import {NavigationService} from "../../../services/navigations/navigation.service";
import {TeamService} from "../../../services/team/team.service";
import {TeamMembersModule} from "../../team-members/team-members.module";
import {BehaviorSubject, Subscription} from "rxjs";
import {SelectComponentComponent} from "../../select-component/select-component.component";
import {getDetailPointOfAction} from "../../../services/variables";
import {catchError} from "rxjs/operators";
import {HttpClient} from "@angular/common/http";
import {PointOfActionModel} from "../../../models/map/point-of-action.model";
import {StripUrlPipe} from "../../../pipes/strip-url.pipe";
import {PostCardComponent} from "../../post-card/post-card.component";

@Component({
  selector: 'app-map-side-panel',
  templateUrl: './map-side-panel.component.html',
  styleUrls: ['./map-side-panel.component.scss'],
  imports: [
    IonicModule,
    PhotoBoxComponent,
    TranslatePipe,
    MapPrimaryButtonComponent,
    MapToggleBarComponent,
    MapStatisticCirclesComponent,
    MapMomentComponent,
    NgSwitch,
    NgSwitchCase,
    NgForOf,
    NgIf,
    ActionPointListModule,
    CommonMyApModule,
    TeamListModule,
    FollowerListModule,
    CompanyEmployeeModule,
    MapSecondaryButtonComponent,
    MapToggleBarComponent,
    TeamMembersModule,
    MapToggleBarComponent,
    StripUrlPipe,
    PostCardComponent,
  ],
  standalone: true
})
export class MapSidePanelComponent {
  @Output() close: EventEmitter<void> = new EventEmitter<void>();

  @Input() set panelIsOpen(value: boolean) {
    this._panelIsOpen = value;

    if (value) {
      this.currentSegment = 'posts';

      if (this._marker?.type === 'organization' && !this.organization && !this.pointOfAction) {
        this.loadCompany(this._marker.id);
      } else if (this._marker?.type === 'space' && !this.space && !this.pointOfAction) {
        this.loadSpace(this._marker.id);
      }
      if (this._marker?.type === 'poa' && !this.organization && !this.space) {
        const pointOfActionId: number = this._marker.id;

        let url = `${getDetailPointOfAction}/${pointOfActionId}`;

        this.http.get<ApiResponse>(url, {
          headers: this.authService.jsonheader
        })
          .pipe(
            catchError(handleError)
          )
          .subscribe({
            next: (response) => {
              this.pointOfAction = response.data;
            },
            error: (err) => console.error('API error:', err)
          });
      }
    }
  }

  @Input() set marker(value: MapMarkerModel | undefined) {
    if (!value) {
      this._marker = undefined;
      return;
    }

    this._marker = value;

    if (this._marker?.type === 'organization') {
      this.organization = null;
      this.space = null;
      this.pointOfAction = null;
      this.currentSegment = 'posts';
    } else if (this._marker?.type === 'space') {
      this.space = null;
      this.organization = null;
      this.pointOfAction = null;
      this.currentSegment = 'posts';
    } else if (this._marker?.type === 'poa') {
      this.organization = null;
      this.space = null;
      this.pointOfAction = null;

      const pointOfActionId: number = this._marker.id;
      const url = `${getDetailPointOfAction}/${pointOfActionId}`;

      this.http.get<ApiResponse>(url, {
        headers: this.authService.jsonheader
      })
        .pipe(catchError(handleError))
        .subscribe({
          next: (response) => {
            this.pointOfAction = response.data;
          },
          error: (err) => console.error('API error:', err)
        });
    }
  }

  private _marker?: MapMarkerModel;
  private _panelIsOpen: boolean = false;

  public organization: CompanyModel | null = null;
  public space: TeamModel | null = null;
  public user: UserModel = null;
  public pointOfAction: PointOfActionModel = null;
  public errorMessage: string[] = [];
  public currentSegment: string = 'posts';

  get companyPosts(): PostModel[] {
    return this.dataService.posts.items;
  }

  get actionPoints(): PostModel[] {
    return this.dataService.actionPoints.items;
  }

  get teams(): TeamModel[] {
    return this.dataService.teams.items;
  }

  get followers(): UserModel[] {
    return this.dataService.followers.items;
  }

  get employees(): UserModel[] {
    return this.dataService.employees.items;
  }

  get spacePosts(): PostModel[] {
    return this.dataService.spacePosts.items;
  }

  get spaceMembers(): UserModel[] {
    return this.dataService.spaceMembers.items;
  }

  get spaceActionPoints(): PostModel[] {
    return this.dataService.spaceActionPoints.items;
  }

  get loading(): boolean {
    return this.dataService.posts.loading || this.dataService.spacePosts.loading;
  }

  get panelIsOpen(): boolean {
    return this._panelIsOpen;
  }

  get marker(): MapMarkerModel | undefined {
    return this._marker;
  }

  // Company loaders
  private companyLoaders: Record<string, () => void> = {
    posts: (): void => this.dataService.loadCompanyPosts(this.organization?.id),
    action_points: (): void => this.dataService.loadCompanyActionPoints(this.organization?.id),
    teams: (): void => this.dataService.loadCompanyTeams(this.organization?.id),
    follower: (): void => this.dataService.loadCompanyFollowers(this.organization?.id),
    employees: (): void => this.dataService.loadCompanyEmployees(this.organization?.id),
  };

  // Space loaders
  private spaceLoaders: Record<string, () => void> = {
    posts: (): void => this.dataService.loadSpacePosts(this.space?.id),
    action_points: (): void => this.dataService.loadSpaceActionPoints(this.space?.id),
    members: (): void => this.dataService.loadSpaceMembers(this.space?.id),
  };

  private companyService: CompanyService = inject(CompanyService);
  private teamService: TeamService = inject(TeamService);
  private authService: AuthService = inject(AuthService);
  private dataService: SidePanelDataService = inject(SidePanelDataService);
  private feedService: FeedService = inject(FeedService);
  public globalService: GlobalService = inject(GlobalService);
  private navigationService: NavigationService = inject(NavigationService);
  private modalCtrl: ModalController = inject(ModalController);
  private http: HttpClient = inject(HttpClient);

  ngOnInit(): void {
    this.user = this.authService.user;
  }

  public loadCompany(id: number | string): void {
    this.companyService.apiViewCompany(id).subscribe((result: ApiResponse): void => {
      if (result.status) {
        this.organization = result.data as CompanyModel;
        this.space = null;
        this.pointOfAction = null;
        this.dataService.reset();
        this.dataService.loadCompanyPosts(this.organization.id);
      } else {
        this.errorMessage = result.error ?? ['Unknown error'];
      }
    });
  }

  public loadSpace(id: number | string): void {
    this.teamService.apiViewTeam(id).subscribe((result: ApiResponse): void => {
      if (result.status) {
        this.space = result?.data;
        this.organization = null;
        this.pointOfAction = null;
        this.dataService.reset();
        this.dataService.loadSpacePosts(this.space.id);

        this.teamService.teamEvent.next(result.data);
        this.globalService.postactionsurveyEvent.next({type: 'team', id: result.data.id});
      } else {
        this.errorMessage = result.error;
      }
    });
  }

  public onSegmentChanged(value: string): void {
    this.currentSegment = value;
    this.dataService.reset();

    if (this.organization) {
      this.companyLoaders[value]?.();
    } else if (this.space) {
      this.spaceLoaders[value]?.();
    }
  }

  public fullLoaded(): boolean {
    if (this.organization) {
      return !this.dataService[this.currentSegment]?.maxDataLoaded;
    } else if (this.space) {
      return !this.dataService[`space${this.capitalize(this.currentSegment)}`]?.maxDataLoaded;
    }
    return false;
  }

  public loadData(event: any): void {
    if (this.organization) {
      this.companyLoaders[this.currentSegment]?.();
    } else if (this.space) {
      this.spaceLoaders[this.currentSegment]?.();
    }
    event.target.complete();
  }

  public async clickFollow(company: CompanyModel): Promise<void> {
    if (!company) return;

    company.isFollowed = true;
    try {
      const result: boolean = await this.feedService.followCompany(company, this.user);
      if (!result) {
        company.isFollowed = false;
        await this.isAnonymousAction();
      }
    } catch {
      company.isFollowed = false;
    }
  }


  public async clickUnfollow(company: CompanyModel): Promise<void> {
    if (!company) return;

    company.isFollowed = false;
    try {
      const result: boolean = await this.feedService.unfollowCompany(company);
      if (!result) {
        company.isFollowed = true;
      }
    } catch {
      company.isFollowed = true;
    }
  }

  public async openCompanyProfileById(id: number, isAdmin: boolean): Promise<void> {
    await this.navigationService.openCompanyProfileById(id, isAdmin);
    this.close.emit();
  }

  public async openSpaceProfile(space: TeamModel): Promise<void> {
    await this.navigationService.openTeamProfile(space);
    this.close.emit();
  }

  public async joinTeamModal(): Promise<void> {
    if (this.authService.isAuthenticated) {
      const mySubject = new BehaviorSubject<any>(null);

      const modal: HTMLIonModalElement = await this.modalCtrl.create({
        component: SelectComponentComponent,
        id: 'team-select-modal',
        backdropDismiss: true,
        componentProps: {
          mySubject,
          teamId: this.space.id,
        },
      });

      await modal.present();

      const sub: Subscription = mySubject.subscribe((value: any): void => {
        if (value !== null) {
          this.teamService.joinOrLeaveTeam(this.space.id, value, this.space)
            .subscribe((result: ApiResponse): void => {
              if (result.status) {
                this.globalService.presentToast(result.message);
              } else {
                this.globalService.presentToast(result.error[0]);
              }
            });
        }
      });

      modal.onDidDismiss().then((): void => {
        sub.unsubscribe();
      });
    } else {
      await this.globalService.openLoginModal();
    }
  }

  public async openActionPoint(actionPointId: number): Promise<void> {
    await this.navigationService.openActionPointDetail(actionPointId);
    this.closePanel();
  }


  public closePanel(): void {
    this.dataService.reset();
    this.organization = null;
    this.space = null;
    this._marker = null;
    this.pointOfAction = null;
    this.currentSegment = 'posts';
    this.close.emit();
  }

  //----------------------- Helper methods -----------------------//

  private async isAnonymousAction(): Promise<void> {
    if (!(this.user && this.authService.isAuthenticated)) {
      await this.globalService.openLoginModal();
    }
  }

  private capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
}
